/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * COMPONENTE: SessaoExpiradaOverlay
 * VERSÃO:  2.1.0
 * ------------------------------------------------------------
 * NOVIDADES v2.1.0:
 *   ✔ Bloqueia scroll do body
 *   ✔ Bloqueia interação com o app por baixo
 *   ✔ Redireciona em 5s (sem chance de voltar)
 * ============================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { LogIn, ShieldAlert } from 'lucide-react';
import { onSessaoExpirada } from '../services/sessaoGuard';

const SEGUNDOS_REDIRECT = 5;

export default function SessaoExpiradaOverlay() {
  const [aberto, setAberto] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [segundos, setSegundos] = useState(SEGUNDOS_REDIRECT);
  const timerRef = useRef(null);

  useEffect(() => {
    const remover = onSessaoExpirada((event) => {
      setAberto(true);
      setDetalhe(event.detail);
      setSegundos(SEGUNDOS_REDIRECT);
    });
    return remover;
  }, []);

  /* Bloqueia scroll e interação do app quando aberto */
  useEffect(() => {
    if (!aberto) return undefined;

    const htmlOriginal = document.documentElement.style.overflow;
    const bodyOriginal = document.body.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = htmlOriginal;
      document.body.style.overflow = bodyOriginal;
    };
  }, [aberto]);

  /* Contador + redirect automático */
  useEffect(() => {
    if (!aberto) return undefined;

    timerRef.current = setInterval(() => {
      setSegundos((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          irParaLogin();
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [aberto]);

  const irParaLogin = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(';').forEach((c) => {
        const nome = c.split('=')[0].trim();
        document.cookie = `${nome}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });
    } catch { /* ignora */ }
    window.location.href = '/';
  };

  if (!aberto) return null;

  const motivoTxt = detalhe?.motivo === 'sessao_expirada'
    ? 'Sua sessão expirou por inatividade.'
    : 'Sua sessão foi encerrada.';

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/98 backdrop-blur-xl flex items-center justify-center p-6 overflow-hidden"
      onClickCapture={(e) => {
        /* Impede qualquer clique de passar para o app */
        e.stopPropagation();
      }}
    >
      <div className="bg-slate-900 border border-amber-400/30 rounded-[2rem] p-10 max-w-lg w-full text-center shadow-2xl">

        <div className="w-20 h-20 bg-amber-400/10 border border-amber-400/30 rounded-3xl flex items-center justify-center mx-auto mb-6">
          <ShieldAlert size={36} className="text-amber-400" />
        </div>

        <h2 className="text-white font-black uppercase tracking-wider text-xl mb-3">
          Sessão Expirada
        </h2>

        <p className="text-slate-400 text-[12px] leading-relaxed mb-6">
          {motivoTxt} Você será redirecionado para o login.
        </p>

        <div className="bg-slate-950 border border-white/10 rounded-2xl p-6 mb-6">
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">
            Redirecionando em
          </span>
          <span className="text-4xl font-mono font-black text-amber-400">
            {segundos}
          </span>
          <span className="text-amber-400/50 text-xl font-black ml-2">
            s
          </span>
        </div>

        <button
          onClick={irParaLogin}
          className="w-full py-4 rounded-2xl bg-amber-400 text-slate-950 font-black uppercase text-[11px] tracking-widest hover:bg-white transition-all flex items-center justify-center gap-3"
        >
          <LogIn size={16} />
          Ir para o Login agora
        </button>

        <p className="text-[8px] text-slate-600 uppercase tracking-widest mt-6">
          Seus dados locais serão limpos por segurança
        </p>
      </div>
    </div>
  );
}